export const UNEXPECTED_RESPONSE_MESSAGE =
  "The server returned an unexpected response. Try again.";

/**
 * @param {string} text Raw response body
 * @returns {{ ok: true, data: unknown } | { ok: false }}
 */
export function parseJsonResponseBody(text) {
  const trimmed = text.trim();
  if (!trimmed) {
    return { ok: false };
  }

  try {
    return { ok: true, data: JSON.parse(trimmed) };
  } catch {
    return { ok: false };
  }
}

/** @type {ReadonlySet<string>} */
const PEOPLE_FORM_FIELDS = new Set([
  "department",
  "start_on",
  "end_on",
  "role",
]);

/** @type {ReadonlySet<string>} */
const INTERLACED_STAFF_FORM_FIELDS = new Set(["role", "state"]);

/**
 * Normalize JSON error payloads (Rails `render json: { errors: ... }`, etc.)
 * into a flat list of user-facing strings.
 *
 * Supports:
 * - `{ errors: ["a", "b"] }`
 * - `{ error: "msg" }`
 * - `{ errors: { field: ["msg"] } }` (ActiveModel-style hash)
 *
 * @param {unknown} data Parsed JSON body
 * @param {string} [fallbackMessage="Could not save changes."]
 * @returns {string[]}
 */
export function normalizeResponseErrors(
  data,
  fallbackMessage = "Could not save changes.",
) {
  let messages = [];

  if (!data || typeof data !== "object") {
    messages = [fallbackMessage];
  } else {
    const raw = data.errors ?? data.error;
    if (Array.isArray(raw)) {
      messages = raw.map(String);
    } else if (typeof raw === "string") {
      messages = [raw];
    } else if (raw && typeof raw === "object") {
      messages = Object.values(raw).flat().map(String);
    } else {
      messages = [fallbackMessage];
    }
  }

  const nonEmpty = messages.map((s) => s.trim()).filter(Boolean);
  return nonEmpty.length > 0 ? nonEmpty : [fallbackMessage];
}

/**
 * Split People PATCH errors into fields rendered on the form vs. general banner
 * (e.g. `organization_id`, `:base`).
 *
 * @param {unknown} data Parsed JSON body
 * @param {string} [fallbackMessage="Could not save changes."]
 * @returns {{ fieldErrors: Record<string, string[]>, generalMessages: string[] }}
 */
export function partitionPeopleFormErrors(
  data,
  fallbackMessage = "Could not save changes.",
) {
  /** @type {Record<string, string[]>} */
  const fieldErrors = {};
  /** @type {string[]} */
  const generalMessages = [];

  if (!data || typeof data !== "object") {
    generalMessages.push(fallbackMessage);
    return { fieldErrors, generalMessages };
  }

  const raw = data.errors ?? data.error;

  if (Array.isArray(raw)) {
    const list = raw.map(String).map((s) => s.trim()).filter(Boolean);
    return {
      fieldErrors,
      generalMessages: list.length > 0 ? list : [fallbackMessage],
    };
  }

  if (typeof raw === "string") {
    const t = raw.trim();
    generalMessages.push(t || fallbackMessage);
    return { fieldErrors, generalMessages };
  }

  if (raw && typeof raw === "object") {
    for (const [key, val] of Object.entries(raw)) {
      const msgs = (Array.isArray(val) ? val : [val])
        .map(String)
        .map((s) => s.trim())
        .filter(Boolean);
      if (msgs.length === 0) continue;
      if (PEOPLE_FORM_FIELDS.has(key)) {
        fieldErrors[key] = msgs;
      } else {
        generalMessages.push(...msgs);
      }
    }
    if (
      Object.keys(fieldErrors).length === 0 &&
      generalMessages.length === 0
    ) {
      generalMessages.push(fallbackMessage);
    }
    return { fieldErrors, generalMessages };
  }

  generalMessages.push(fallbackMessage);
  return { fieldErrors, generalMessages };
}

/**
 * Split Interlaced Staff PATCH errors into form fields vs. general banner.
 *
 * @param {unknown} data Parsed JSON body
 * @param {string} [fallbackMessage="Could not save changes."]
 * @returns {{ fieldErrors: Record<string, string[]>, generalMessages: string[] }}
 */
export function partitionInterlacedStaffFormErrors(
  data,
  fallbackMessage = "Could not save changes.",
) {
  /** @type {Record<string, string[]>} */
  const fieldErrors = {};
  /** @type {string[]} */
  const generalMessages = [];

  if (!data || typeof data !== "object") {
    generalMessages.push(fallbackMessage);
    return { fieldErrors, generalMessages };
  }

  const raw = data.errors ?? data.error;

  if (Array.isArray(raw)) {
    const list = raw.map(String).map((s) => s.trim()).filter(Boolean);
    return {
      fieldErrors,
      generalMessages: list.length > 0 ? list : [fallbackMessage],
    };
  }

  if (typeof raw === "string") {
    const t = raw.trim();
    generalMessages.push(t || fallbackMessage);
    return { fieldErrors, generalMessages };
  }

  if (raw && typeof raw === "object") {
    for (const [key, val] of Object.entries(raw)) {
      const msgs = (Array.isArray(val) ? val : [val])
        .map(String)
        .map((s) => s.trim())
        .filter(Boolean);
      if (msgs.length === 0) continue;
      if (INTERLACED_STAFF_FORM_FIELDS.has(key)) {
        fieldErrors[key] = msgs;
      } else {
        generalMessages.push(...msgs);
      }
    }
    if (
      Object.keys(fieldErrors).length === 0 &&
      generalMessages.length === 0
    ) {
      generalMessages.push(fallbackMessage);
    }
    return { fieldErrors, generalMessages };
  }

  generalMessages.push(fallbackMessage);
  return { fieldErrors, generalMessages };
}
